import { useCallback } from 'react';
import { useRouteMatch } from 'react-router';

import {
  Tabs,
  Tab,
  WidgetSettingsBuilder,
  Breadcrumbs,
  Button,
  DynamicTopPanel,
  SubmitButton,
  Form,
} from '../../components';
import { NewAgentTabs } from './NewAgentTabs';
import { useNewAgent } from './hooks/useNewAgent';
import { usePermissionAllowed } from '../../hooks';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { useFormSchema } from '../agent/hooks/useFormSchema';
import type { AgentForm } from '../../utilities';
import type { FormJsonData } from '../../api/form-controller';

type TabsEnum = 'newAgent' | 'widgetFormSettings';

const NewAgentPageTabs = () => {
  const editDisabled = !usePermissionAllowed([PERMISSIONS.EDIT_AGENTS]);

  const {
    form,
    onChange,
    adding,
    onSubmit,
    setWidgetData,
    widgetData,
    formId,
    setFormId,
    ttsPrimary,
    ttsBackup,
    sttPrimary,
    sttBackup,
    llmPrimary,
    llmBackup,
    validity,
    knowledgeData,
    activeTab,
    setActiveTab,
    importWidgetSettingsFromAgent,
    importingWidgetSettings,
    agentsOptions,
    ...rest
  } = useNewAgent();
  const { params } = useRouteMatch<{ organization_id: string }>();

  const { data: schema, intializing } = useFormSchema('agentWidgetSettings');

  const handleFormChange = useCallback(
    (data: AgentForm) => {
      onChange(data);
    },
    [onChange]
  );

  const handleWidgetSettingsChange = useCallback(
    (data: Record<string, unknown>) => {
      const formData = {
        ...form,
        widget_settings: data ? JSON.stringify(data) : '',
      };
      setWidgetData(data);
      handleFormChange(formData as any);
    },
    [form, handleFormChange, setWidgetData]
  );

  const handleApplyFromAgent = useCallback(
    async (sourceAgentId: string) => {
      await importWidgetSettingsFromAgent(sourceAgentId);
    },
    [importWidgetSettingsFromAgent]
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      if (tab !== 'widgetFormSettings') {
        setFormId(f => f + 1);
      }
    },
    [setFormId]
  );

  return (
    <Form
      key={String(formId || 'NEW')}
      initialValue={form as any}
      onChange={handleFormChange}
    >
      <div className="flex gap-md flex-wrap justify-between items-center mb-md">
        <Breadcrumbs
          className="text-small"
          subtitle="Agents"
          title="New Agent"
          subtitleUrl={`/agents/${params.organization_id}`}
        />
      </div>
      <Tabs<TabsEnum>
        tabs={[
          { name: 'newAgent', label: 'Agent' },
          { name: 'widgetFormSettings', label: 'Widget Settings' },
        ]}
        contentClassName="mt-md"
        keepMounted
        onChange={handleTabChange}
      >
        <Tab name="newAgent">
          <NewAgentTabs
            form={form!}
            editDisabled={editDisabled}
            onChange={handleFormChange}
            ttsPrimary={ttsPrimary}
            ttsBackup={ttsBackup}
            sttPrimary={sttPrimary}
            sttBackup={sttBackup}
            llmPrimary={llmPrimary}
            llmBackup={llmBackup}
            validity={validity}
            knowledgeData={knowledgeData || []}
            editing={false}
            onSubmit={onSubmit}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            {...rest}
          />
        </Tab>
        <Tab name="widgetFormSettings">
          <WidgetSettingsBuilder
            onChange={handleWidgetSettingsChange}
            widgetSettings={widgetData}
            schemaData={schema as FormJsonData}
            intializing={intializing}
            sourceAgentOptions={agentsOptions}
            currentAgentName={form?.title || 'New agent'}
            onApplyFromAgent={handleApplyFromAgent}
            applyFromAgentLoading={importingWidgetSettings}
          />
        </Tab>
      </Tabs>
      {!editDisabled && (
        <DynamicTopPanel>
          <div className="flex flex-row gap-x-md justify-center items-center">
            <Button
              label="Cancel"
              url={`/agents/${params.organization_id}`}
              type="contrast"
              size="medium"
            />
            <SubmitButton
              label="Save"
              size="medium"
              type="primary"
              loading={adding}
              disabled={adding}
              onClick={onSubmit}
            />
          </div>
        </DynamicTopPanel>
      )}
    </Form>
  );
};

export default NewAgentPageTabs;
